import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { notifications } from '@mantine/notifications';
import type { UserPermissions } from '@/hooks/usePermissions';

export interface CustomRole {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  permissions: UserPermissions;
  created_at: string;
  updated_at: string;
}

interface CreateCustomRoleInput {
  organizationId: string;
  name: string;
  description?: string;
  permissions: UserPermissions;
}

interface UpdateCustomRoleInput {
  id: string;
  name?: string;
  description?: string;
  permissions?: UserPermissions;
}

// Fetch all custom roles for an organization
async function fetchCustomRoles(organizationId: string): Promise<CustomRole[]> {
  const { data, error } = await supabase
    .from('custom_roles')
    .select('*')
    .eq('organization_id', organizationId)
    .order('name', { ascending: true });

  if (error) throw error;
  return data || [];
}

// Create a custom role
async function createCustomRole(input: CreateCustomRoleInput): Promise<CustomRole> {
  const { data, error } = await supabase
    .from('custom_roles')
    .insert({
      organization_id: input.organizationId,
      name: input.name,
      description: input.description || null,
      permissions: input.permissions,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Update a custom role
async function updateCustomRole(input: UpdateCustomRoleInput): Promise<CustomRole> {
  const updateData: any = { updated_at: new Date().toISOString() };
  if (input.name !== undefined) updateData.name = input.name;
  if (input.description !== undefined) updateData.description = input.description;
  if (input.permissions !== undefined) updateData.permissions = input.permissions;

  const { data, error } = await supabase
    .from('custom_roles')
    .update(updateData)
    .eq('id', input.id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Delete a custom role
async function deleteCustomRole(roleId: string): Promise<void> {
  const { error } = await supabase
    .from('custom_roles')
    .delete()
    .eq('id', roleId);

  if (error) throw error;
}

// Assign a custom role to a user (copies permissions)
async function assignCustomRole(memberId: string, customRoleId: string | null): Promise<void> {
  let permissions = {};

  // If assigning a role, fetch its permissions
  if (customRoleId) {
    const { data: role, error: roleError } = await supabase
      .from('custom_roles')
      .select('permissions')
      .eq('id', customRoleId)
      .single();

    if (roleError) throw roleError;
    permissions = role.permissions;
  }

  // Update member with custom role and copy permissions
  const { error } = await supabase
    .from('organization_members')
    .update({
      custom_role_id: customRoleId,
      permissions,
    })
    .eq('id', memberId);

  if (error) throw error;
}

// React Query Hooks

export function useCustomRoles(organizationId: string) {
  return useQuery({
    queryKey: ['custom-roles', organizationId],
    queryFn: () => fetchCustomRoles(organizationId),
    enabled: !!organizationId,
  });
}

export function useCreateCustomRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCustomRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-roles'] });
      notifications.show({
        title: 'Role created',
        message: 'Custom role has been created successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to create custom role',
        color: 'red',
      });
    },
  });
}

export function useUpdateCustomRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCustomRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-roles'] });
      notifications.show({
        title: 'Role updated',
        message: 'Custom role has been updated successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update custom role',
        color: 'red',
      });
    },
  });
}

export function useDeleteCustomRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCustomRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-roles'] });
      notifications.show({
        title: 'Role deleted',
        message: 'Custom role has been deleted successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete custom role',
        color: 'red',
      });
    },
  });
}

export function useAssignCustomRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId, customRoleId }: { memberId: string; customRoleId: string | null }) =>
      assignCustomRole(memberId, customRoleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-members'] });
      notifications.show({
        title: 'Role assigned',
        message: 'Custom role has been assigned to the user',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to assign custom role',
        color: 'red',
      });
    },
  });
}
